import { Check, Eye, Send } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Banner } from '@/components/feedback/Banner'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'
import { QuoteStatusPill } from '@/components/ui/StatusPill'
import { CAP, can } from '@/lib/can'
import { placeName } from '@/lib/countries'
import { MODE_ICONS } from '@/lib/icons'
import {
  useQuote,
  useQuoteTeam,
  useConvertQuote,
  useReopenQuote,
  useOfferPreview,
  type OfferLanguage,
  useSendQuote,
  useUpdateQuote,
  toQuoteUpdatePayload,
} from '@/features/quotes/api/useQuote'
import { useSession } from '@/features/auth/api/useSession'
import { ChatDock, ChatDockBadge } from '@/features/chat/components/ChatDock'
import {
  canSeeRecordChat,
  useRecordConversation,
} from '@/features/chat/api/useRecordChat'
import { ConvertQuoteSheet } from '@/features/quotes/components/ConvertQuoteSheet'
import { MarginPanel } from '@/features/quotes/components/MarginPanel'
import { OfferPreviewSheet } from '@/features/quotes/components/OfferPreviewSheet'
import { OfferSendSheet } from '@/features/quotes/components/OfferSendSheet'
import { QuoteFormSheet } from '@/features/quotes/components/QuoteFormSheet'
import { QuoteHistorySection } from '@/features/quotes/components/QuoteHistorySection'
import { QuotePricingCard } from '@/features/quotes/components/QuotePricingCard'
import { QuoteTeamSection } from '@/features/quotes/components/QuoteTeamSection'
import {
  QuoteClientBlock,
  QuotePreviewSection,
} from '@/features/quotes/components/QuoteDetailSections'
import {
  QuoteMoreMenu,
  type QuoteMorePanel,
} from '@/features/quotes/components/QuoteMoreMenu'
import {
  QuoteSection,
  QuoteSectionBar,
  SECTION_BAR_HEIGHT,
} from '@/features/quotes/components/QuoteSectionNav'
import {
  CargoSection,
  CostLineSection,
  RevisionsSection,
  RouteOptionsSection,
  TermsSection,
} from '@/features/quotes/components/QuoteSections'
import {
  QuotePage,
  QuotePageColumns,
  QuotePageHeader,
} from '@/features/quotes/components/QuotePageChrome'
import {
  QuoteMoneyCard,
  QuoteOfferFactsCard,
  QuoteReadinessCard,
} from '@/features/quotes/components/QuoteAsideCards'
import {
  convertErrorMessage,
  convertOwnerError,
  errorMessage,
  issueText,
  sendBlockedReason,
  sendErrorMessage,
} from '@/features/quotes/quoteText'
import {
  quoteReadiness,
  recipientState,
  sectionMarkers,
} from '@/features/quotes/quoteReadiness'
import { QUOTE_SECTIONS, type QuoteSectionKey } from '@/features/quotes/sectionSpy'
import { useSectionSpy } from '@/features/quotes/useSectionSpy'
import type { CargoPosition, CostLine } from '@/types/domain'
import { useTranslation } from 'react-i18next'

/* `errorMessage` used to be defined here, identically to the copy in
   quoteText. That local copy is what kept this page on the generic wording:
   `sendErrorMessage` was extracted precisely so the branding refusal could name
   Settings → Entities, and nothing imported it, because the name it would have
   shadowed was already in scope. Imported now, and the duplicate is gone. */

/* The sections are the SHARED ones (QuoteSections), not copies.
 *
 * `CargoSection`, `RouteOptionsSection`, `RevisionsSection` and the header's
 * `TotalFigure` all used to be defined here as well, beside the identical
 * exports the builder imports — and they had already drifted: the copy of the
 * cargo table on this page had lost the tint under its totals row, so the same
 * table read as four positions on one surface and three plus a footer on the
 * other. `issueText` was a third copy of the same sentence.
 *
 * Deleted rather than re-synced, exactly as the costing and the terms were
 * before them. The two quote surfaces are one implementation hosted twice, and
 * that is only true while nothing on either of them is a local re-statement of
 * the other.
 *
 * `HistorySection` was the last one still defined here, together with the action
 * catalogue and the wording helper it needed, and it is now
 * `QuoteHistorySection` beside its neighbours. It was the only reason this page
 * imported a query hook, a timestamp formatter and four Card parts it uses
 * nowhere else — which is what a section left behind on a page always costs. */

/* THE QUOTE AS ONE SHEET (S-3, decision s3-quote-one-sheet, superseding
 * UX-DR-9's "sections are tabs, not a stack").
 *
 * Five sections in one scrolling column, in the order the work happens: who it
 * is for and what is shipped, how it travels, what it costs, on what terms —
 * and, on a draft the reader may send, a last look before it goes. A sticky
 * bar of links sits above them and carries Send, so the decision and the way
 * to make it never scroll away; that was the argument for tabs, and the bar
 * keeps it without hiding four fifths of the quote behind a switcher.
 *
 * The three backward-looking views — the revisions issued, the action log and
 * the team — are not sections. They open in side sheets from the header's «⋯»
 * menu.
 *
 * Nothing is read from or written to the URL: `?tab=` is gone, a stale link
 * carrying it opens the sheet at the top, and a scroll position is not an
 * address. */
const SECTIONS_WITHOUT_PREVIEW: readonly QuoteSectionKey[] = QUOTE_SECTIONS.filter(
  (key) => key !== 'preview',
)

export function QuoteDetailPage() {
  const { t, i18n } = useTranslation()
  const { quoteId = '' } = useParams()
  const { data: record, isPending, isError, error, refetch } = useQuote(quoteId)
  // Declared before the early returns: hooks cannot be called conditionally,
  // and the loading and error branches below both return.
  const [cargoTarget, setCargoTarget] = useState<CargoPosition | 'new' | null>(null)
  const [costLineTarget, setCostLineTarget] = useState<CostLine | 'new' | null>(null)
  const [sendOpen, setSendOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  /* The CLIENT's language, for the offer — the one fact the preview sheet and
     the send sheet must agree on, which is why it is held here and not in
     either of them. Previewing in one language and issuing in another is the
     one way this pair of sheets can lie to a reader who checked the document
     before pressing send.

     Seeded from the INTERFACE, and this is a guess rather than a lookup: a
     quote records no language for its client, so the best available answer is
     that a manager working in English is probably quoting a client who reads
     it. The server's own default (Russian) still applies to anything that never
     reaches this page. */
  const [offerLanguage, setOfferLanguage] = useState<OfferLanguage>(
    i18n.language.startsWith('en') ? 'en' : 'ru',
  )
  const [editing, setEditing] = useState(false)
  /* The conversion confirmation (QW-11). Page state rather than the sheet's,
     because the header button opens it and the mutation it confirms is the
     page's — the sheet only shows and asks. */
  const [convertOpen, setConvertOpen] = useState(false)
  /* The PHONE's full-screen conversation. Owned by the page rather than by the
     dock, because the badge that opens it lives in the header strip and the
     panel is mounted at the end of this tree — one piece of state with two
     readers beats an imperative handle between siblings. The desktop dock's
     own collapsed/expanded choice is not here: it is a remembered per-reader
     preference the dock owns, because it outlives this page (see ChatDock). */
  const [chatOpen, setChatOpen] = useState(false)
  /* Which of the «⋯» menu's side sheets is open, if any. One value, because
     they are three views of the same record and never stand on top of each
     other. */
  const [panel, setPanel] = useState<QuoteMorePanel | null>(null)
  const send = useSendQuote(quoteId)
  /* Fetched only while the sheet is open: composing an offer reads the cargo,
     the costing and the routings server-side, and doing that on every visit to
     a quote would be a document nobody asked to see. */
  const preview = useOfferPreview(quoteId, {
    enabled: previewOpen,
    language: offerLanguage,
  })
  const update = useUpdateQuote()
  const reopen = useReopenQuote(quoteId)
  const convert = useConvertQuote()
  const navigate = useNavigate()

  /* CHAT ON A QUOTE (0051). The same panel the case detail renders, over the
     same service, on quote's own row scope — narrowed to the people staffed on
     this quote. Not rendered at all for anyone else: every request it made
     would answer 404, and a badge that opens onto an error is worse than no
     badge.
     The owner id IS handed over, and an empty string here was a bug: the quote's
     owner is seeded onto `quote_team` at create (migration 0028), but Unassign
     refuses only the removal that would EMPTY the team, so an owner can be taken
     off their own quote. StaffedOrOwned still serves them the conversation
     (`q.owner_id = $1 OR` the team predicate) and this page rendered nothing.
     Declared here, above the early returns: hooks cannot be called
     conditionally, and both the loading and the error branch below return —
     so `record` may be undefined for one render, which reads as "not yet". */
  const { data: user } = useSession()
  const team = useQuoteTeam(quoteId)
  const canChat = canSeeRecordChat(user, record?.ownerId ?? '', team.data ?? [])
  const conversation = useRecordConversation('quote', quoteId, canChat)

  /* SEND AND PREVIEW ARE A CAPABILITY (S-3, L-7). Pricing and Operations do
     not hold `quote.send`, and the server refuses them; a control a role will
     never be allowed to press is not shown at all (frontend/README,
     Authorization). Read through `can`, never a role name — a custom role
     granted it sees both. */
  const canSend = can(user, CAP.quoteSend)
  const isDraft = record?.status === 'draft'
  /* The preview SECTION exists on a draft the reader may send; the bar links
     to it only then. Two module constants rather than a filter here, so the
     spy's listener is keyed on a stable list. */
  const showPreviewSection = isDraft && canSend
  const spy = useSectionSpy(
    showPreviewSection ? QUOTE_SECTIONS : SECTIONS_WITHOUT_PREVIEW,
    SECTION_BAR_HEIGHT,
  )

  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('quotes.loading')}>
        <Skeleton className="h-20 w-full rounded-[var(--radius-DEFAULT)]" />
        <Skeleton className="h-16 w-full rounded-md" />
        <Skeleton className="h-64 w-full rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }

  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  // An absent checklist is not a clean one: nothing has been checked because
  // nothing was entered, which is why cargo is asked about separately.
  const checklist = record.checklist ?? []
  const hasCargo = (record.cargo ?? []).length > 0
  const blocked = checklist.length > 0
  // The SERVER decides whether an offer may go out (FR-3). Falling back to the
  // local reading only for a response that predates the field — deriving it
  // here in general would let the client and the server disagree about the one
  // question this screen exists to answer.
  const sendable = record.sendable ?? (hasCargo && !blocked)
  // Reopening has no local fallback the way sendable does. Whether a status may
  // open a new revision is a lifecycle rule the client does not hold, so an
  // older server that does not send the field means the button stays off —
  // which is the safe direction, and the refusal would say so anyway.
  const reopenable = record.reopenable ?? false
  // What "Заполнить груз" opens: the position the FIRST listed issue is about,
  // because the banner lists them in a stable order and the top line is the one
  // the eye is on when the button is pressed. An issue about the quote as a
  // whole carries no positionId, and an issue can outlive the row it names if
  // the checklist is a render behind a deletion — either way there is no row to
  // open, so the sheet opens on a new position rather than on nothing at all,
  // which is the state the button was stuck in before.
  const firstBlockedId = checklist.find((issue) => issue.positionId)?.positionId
  const firstBlocked =
    (record.cargo ?? []).find((position) => position.id === firstBlockedId) ?? 'new'
  // Absent until a route option carries one (FR-3, Story 2.4): a header-only
  // quote has a client before it has a routing.
  const ModeIcon = record.mode ? MODE_ICONS[record.mode] : undefined
  /* One reading of «Готовность к отправке» for both the rail's card and the
     bar's markers, so the two cannot disagree (L-4). Drafts only: on a sent
     quote every line is a tick by construction. */
  const readiness = quoteReadiness(record, user)
  /* The address is part of the send rule but not of `sendable` (F-1): the
     server refuses a send with nowhere to go after it has cleared the quote
     itself, so every Send here says so before a sheet opens to refuse it. */
  const sendRefusal = sendBlockedReason(
    { sendable, hasCargo, blocked, recipient: recipientState(record, user) },
    t,
  )
  const editRefusal =
    record.editable === false ? t('quotes.revisions.notEditable') : undefined
  const openEdit = () => {
    update.reset()
    setEditing(true)
  }

  return (
    /* ONE COLUMN, at every width — the same shape the case detail lays out, for
       the same reason. This used to be a flex ROW above lg with a persistent
       chat column on the right, which cost the quote roughly 306px of width on
       every screen. The dock is fixed to the viewport now (0052) and out of
       this page's flow: it is still a sibling below, but only for z-order.
       From xl up an EXPANDED dock is sized around — by the SHELL, whose
       `<main>` reserves its width while it is expanded (N-06, #127), never by
       this page. What answers to the narrower column here is QuotePageColumns,
       whose rail stacks when its own box drops under 61rem.

       QuotePage keeps its own `min-w-0`, which is what stops a wide costing
       table from scrolling the page sideways instead of scrolling inside its
       own card. Nothing here competes with it for width. */
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* The frame is the BUILDER's, shared through QuotePageChrome: the same
         bordered header strip, the same 1fr + 340px body and the same 3.5 gap.
         This page used to draw its own — a bare heading over a full-width column
         at a different gap — and the two routes onto one quote read as two
         products because of it. Nothing about what this page loads, or where its
         controls go, moved with the frame.

         The sheets stay INSIDE it, exactly where they were: they render fixed
         overlays and have no business becoming flex children of the chat row. */}
      <QuotePage>
        <QuotePageHeader
          backTo="/quotes"
          backLabel={t('quotes.backToList')}
          /* The number IS the title here, where the builder titles itself and
             chips the number: on this route the record exists and is what the
             reader came for, and there is no act in progress to name instead. */
          title={record.number}
          badges={<QuoteStatusPill status={record.status} />}
          subtitle={
            <>
              <span className="text-text-body">{record.clientName}</span>
              <span aria-hidden className="text-text-faint">·</span>
              {ModeIcon ? <ModeIcon size={14} aria-hidden /> : null}
              {record.origin && record.destination ? (
                <>
                  {placeName(record.origin, i18n.language)}
                  <span aria-hidden className="text-text-faint">→</span>
                  {placeName(record.destination, i18n.language)}
                </>
              ) : null}
              {/* Who prices it is NOT here: the «Расчёт» card says so, from
                  the served pricing state, on every draft. */}
            </>
          }
          /* The record's actions where the builder puts its step rail. They stay
             in the header rather than moving to a footer bar: the builder's
             footer carries STEP navigation, which this page has none of, and a
             fixed bar holding Send would cover the section the reader is
             checking before they press it. */
          trailing={
            <>
              {/* The phone's way into the conversation, first in the row because
                  it is the one control here that is not about the quote's
                  lifecycle. `lg:hidden` inside it: above the breakpoint the
                  dock is its own way back in — expanded in the corner, or the
                  floating button carrying the same count this badge does
                  (UX-DR-28). */}
              {canChat ? (
                <ChatDockBadge
                  onOpen={() => setChatOpen(true)}
                  unread={conversation?.unread ?? 0}
                />
              ) : null}

              {/* A converted quote is READ-ONLY and offers the way into its case
                  (FR-19, FR-21). Shown first because once a case exists it is the
                  thing the reader came for — the quote behind it is provenance. */}
              {/* A button that NAVIGATES rather than a Link styled as one: this
                  Button renders a real <button> and has no asChild escape, and its
                  variant recipe is not exported for a Link to borrow. The
                  affordance and the destination are identical either way. */}
              {record.status === 'converted' && record.caseId ? (
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/cases/${record.caseId}`)}
                >
                  {t('quotes.actions.openCase', {
                    number: record.caseNumber ?? '',
                  })}
                </Button>
              ) : null}

              {/* Approving is the act that creates the case (FR-19, AD-14): one
                  server transaction writes the case, its rate and its planned
                  lines, or none of them. Offered only on a quote the client can
                  still answer — a converted one already has its case above, and a
                  draft was never sent.

                  Not disabled-with-a-title like the reopen button beside it,
                  because this action is ABSENT rather than refused on states that
                  cannot convert: a Send button greyed out invites a second click,
                  while a missing Approve button says the question is not open.

                  The press ASKS rather than acts (QW-11, walkthrough C-1). It is
                  the one irreversible act on a quote, and it used to fire on a
                  single click under a label that read as bookkeeping; the label
                  now says a case is created, and the sheet previews that case
                  before anything is sent. Opening it clears the last refusal and
                  re-reads the quote and its team, so the preview describes the
                  quote as it stands now rather than as this tab last saw it.

                  The accessible name drops the arrow the visible label carries —
                  a screen reader would announce it as a word.

                  The label is the longest in the strip and may WRAP: on a 320px
                  phone it is wider than the header, and Button is nowrap by
                  default. Wrapping keeps the same words at every width — a
                  shorter phone label would bring back the label C-1 was about.
                  `h-auto` + `min-h-*` keep the one-line height (36px, 44px on a
                  coarse pointer) and let a second line grow the button. */}
              {record.status === 'sent' ? (
                <Button
                  disabled={convert.isPending}
                  aria-label={t('quotes.actions.approveLabel')}
                  className="h-auto min-h-9 py-1.5 leading-snug text-balance whitespace-normal pointer-coarse:h-auto pointer-coarse:min-h-11"
                  onClick={() => {
                    convert.reset()
                    void refetch()
                    void team.refetch()
                    setConvertOpen(true)
                  }}
                >
                  {t('quotes.actions.approve')}
                </Button>
              ) : null}

              {/* EDIT THE HEADER (FR-4, FR-11, FR-59, Story 2.1).
                  PUT /quotes/:id has existed, been guarded and been action-logged
                  since Story 2.1, and until 2026-08-17 nothing in the product called
                  it: `useUpdateQuote` had no consumer and QuoteFormSheet was
                  rendered only by its own test. So a quote's client, validity, terms
                  and header measures could be set once, in the wizard, and never
                  corrected — and Story 2.6's "editing is only possible by opening a
                  new revision, which reopens the editing window" reopened a window
                  with no editor in it.

                  Enablement is the SERVER's `editable`, not a status comparison
                  written again here: the two would drift, and the refusal is what
                  ultimately decides (AD-21). A quote that is not editable keeps the
                  button HOVERABLE through blockedReason rather than taking a plain
                  `disabled`, because `disabled` makes a title unreachable by mouse
                  and by Tab — so the one sentence explaining why the user cannot
                  type would be the one sentence they cannot read. */}
              <Button
                variant="secondary"
                disabled={update.isPending}
                blockedReason={editRefusal}
                onClick={openEdit}
              >
                {t('common.actions.edit')}
              </Button>

              {/* A new revision reopens the editing window on a Sent or Rejected
                  quote (FR-6, FR-7). The already-issued revisions are untouched,
                  which is what makes this safe to offer from a sent offer: what
                  reopens is the live header, and the history stays the record of
                  what the client actually received.

                  Enablement is the SERVER's answer, not a status check written
                  again here — the two would drift, and the refusal is what
                  ultimately decides. */}
              {/* The two refusals are NOT one condition. A rule the reader could
                  act on goes to blockedReason, which keeps the button hoverable so
                  the sentence can actually be read; a mutation in flight stays a
                  real `disabled`, because "Reopening…" on the button already says
                  why and a tooltip repeating it would be noise. */}
              <Button
                variant="secondary"
                disabled={reopen.isPending}
                blockedReason={
                  reopenable ? undefined : t('quotes.revisions.notReopenable')
                }
                onClick={() => reopen.mutate()}
              >
                {reopen.isPending
                  ? t('quotes.actions.reopening')
                  : t('quotes.actions.newRevision')}
              </Button>
              {/* LOOK BEFORE SENDING (FR-71, UX-DR-15) — on a record that has
                  gone out. On a draft the look and the send moved into the
                  sheet itself (S-3): the preview is its last section and Send
                  rides the sticky bar. A sent quote has neither, and its
                  header keeps the look at the paper it was issued as, so the
                  one-sheet page takes nothing away from it.

                  Only for a reader who may send (L-7): the preview is the
                  send's own first step, and Pricing and Operations are
                  refused both. */}
              {!isDraft && canSend ? (
                <Button
                  variant="secondary"
                  iconLeft={Eye}
                  onClick={() => setPreviewOpen(true)}
                >
                  {t('quotes.actions.preview')}
                </Button>
              ) : null}

              {/* After the actions: the revisions, the log and the team, which
                  are about the quote rather than the work on it. */}
              <QuoteMoreMenu
                revisions={(record.revisions ?? []).length}
                team={(team.data ?? []).length}
                onOpen={setPanel}
              />
            </>
          }
        />

        <QuotePageColumns
          /* The rail the builder has, on the surface that had none. Read-only and
             already-loaded: every card takes the record this page fetched and
             adds no request.

             MONEY FIRST, and it is the one card here that repeats a figure from
             the sections beside it. The cost, the customer total and the margin
             are what the quote is FOR, and on this page they used to be
             reachable only by opening the costing and scrolling to MarginPanel.
             That panel stays where it is: it is where the numbers are CHANGED,
             with the markup field and its Apply button. The rail is where they
             are SEEN, beside every section. Neither surface counts anything — both print
             `record.costTotals` — so the duplication cannot become a
             disagreement. */
          aside={
            <>
              <QuoteMoneyCard record={record} />
              <QuoteOfferFactsCard record={record} />
              {/* Readiness is a question about a quote that has not gone out. On
                  a sent or converted record every line is a tick by construction,
                  which is a card spent saying nothing.

                  Only while the rail sits BESIDE the column (owner decision
                  E): at xl and with the columns' box at 61rem or more — the
                  same pair QuotePageColumns lays the rail out by. Otherwise
                  (under xl, or beside an expanded chat dock, N-06) the rail
                  stacks at the very bottom of the page, where an index of the
                  sections is no use — the bar's markers carry the same rows
                  there. */}
              {isDraft ? (
                <div className="hidden xl:@min-[61rem]/quote-columns:block">
                  <QuoteReadinessCard rows={readiness} onGo={spy.go} />
                </div>
              ) : null}
            </>
          }
        >
          {/* The sheet's index, stuck to the top of the scroll container. Send
              rides it on a draft the reader may send, with the server's
              refusal as `blockedReason` and a plain `disabled` only while the
              send is in flight. Markers on drafts only. */}
          <QuoteSectionBar
            sections={showPreviewSection ? QUOTE_SECTIONS : SECTIONS_WITHOUT_PREVIEW}
            active={spy.active}
            onGo={spy.go}
            attach={spy.attach}
            markers={isDraft ? sectionMarkers(readiness) : undefined}
            action={
              showPreviewSection ? (
                /* Sending opens the sheet rather than firing immediately: the
                   recipient has to be on screen before an email leaves
                   permanently (FR-71), and the sheet is where the user sees it.

                   No cargo refuses Send as firmly as an open issue does. The
                   checklist is built FROM the cargo, so an empty one on a quote
                   with no positions means nothing has been checked — not that
                   everything passed. Which of the two it is gets SAID, on a
                   button that stays hoverable to say it. */
                <Button
                  iconLeft={Send}
                  disabled={send.isPending}
                  blockedReason={sendRefusal}
                  onClick={() => setSendOpen(true)}
                >
                  {t('quotes.actions.sendOffer')}
                </Button>
              ) : undefined
            }
          />

          {blocked ? (
            <Banner
              title={t('quotes.checklist.blocked', { count: checklist.length })}
              action={
                /* A sent quote's cargo is not editable, so the banner states the
                   gap without offering a fix that would refuse. */
                record.status === 'draft' ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      // The section first: the sheet belongs to the cargo, and
                      // closing it should leave the reader on the table they
                      // just fixed, wherever on the sheet they pressed this.
                      // Scrolled to, never written to the URL.
                      spy.go('client')
                      setCargoTarget(firstBlocked)
                    }}
                  >
                    {t('quotes.actions.fixCargo')}
                  </Button>
                ) : undefined
              }
            >
              <ul className="mt-0.5 flex flex-col gap-0.5">
                {checklist.map((issue) => (
                  <li key={issue.id}>{issueText(issue, t)}</li>
                ))}
              </ul>
            </Banner>
          ) : !hasCargo ? (
            <Banner tone="info" title={t('quotes.cargo.emptyTitle')}>
              {t('quotes.cargo.emptyDescription')}
            </Banner>
          ) : isDraft && sendRefusal === undefined ? (
            /* "Ready to send" is a statement about a DRAFT, on the readiness
               card's own rule above (QW-9, Q-15): a sent or converted quote
               passes every completeness check by construction, and the banner
               over one read as an invitation to send it again. And only while
               Send itself agrees (F-1): a cleared quote with nowhere to send
               it is not ready. No banner replaces it — the client block's
               recipient line, the bar's marker and Send's own title already
               say what is missing. */
            <Banner tone="info" title={t('quotes.cargo.readyTitle')}>
              <span className="flex items-center gap-1.5">
                <Check size={13} aria-hidden />
                {t('quotes.cargo.readyDescription')}
              </span>
            </Banner>
          ) : null}

          {/* A refused reopen is reported beside the button that asked for it. The
              send's own refusal stays INSIDE the sheet, which is still open on a
              failure with the choice intact. */}
          {reopen.error ? (
            <Banner tone="negative" title={errorMessage(reopen.error, t)} />
          ) : null}

          {/* Approve is reported the same way, and must be: it is the one button
              here that NAVIGATES on success, so a silent failure leaves the user on
              an unchanged page with no case and nothing said — indistinguishable
              from a dead button. The server refuses it for ordinary reasons (a
              quote already converted, a costing that cannot be booked, a user
              without QuoteSend), and each of those is a sentence worth reading. */}
          {/* The refusal lands in the confirmation sheet first (QW-11), which
              is still open when it arrives. This banner is for AFTER the sheet
              is dismissed — so it is drawn only while the sheet is closed, and
              the same sentence never stands twice on one screen. The next
              opening clears it with `convert.reset()`. An owner refusal keeps
              the words the picker gave it: under the generic suffix sentence it
              would read as "some values need attention" with no field in sight. */}
          {convert.error && !convertOpen ? (
            <Banner
              tone="negative"
              title={
                convertOwnerError(convert.error, t) ?? convertErrorMessage(convert.error, t)
              }
            />
          ) : null}

          {/* «Расчёт» (S-2a): where the request for a price stands, and the acts
              that move it. Above the sections rather than in the rail, because
              the rail stacks BELOW the work under xl — on a laptop the one
              control Pricing comes to this page to press would sit under the
              costing. Drafts only: every pricing act refuses a quote that is no
              longer a draft, and on a sent one the state is history, not work.
              It stays here on the one-sheet page too (owner decision D). */}
          {isDraft ? <QuotePricingCard record={record} user={user} /> : null}

          {/* «Клиент и груз»: who it is for, then what is shipped. The cargo
              sheet's target is the PAGE's, because the checklist banner above
              opens it too. */}
          <QuoteSection sectionKey="client">
            <QuoteClientBlock
              record={record}
              user={user}
              onEdit={openEdit}
              editBlockedReason={editRefusal}
              editPending={update.isPending}
            />
            <CargoSection
              record={record}
              target={cargoTarget}
              setTarget={setCargoTarget}
            />
          </QuoteSection>

          <QuoteSection sectionKey="route">
            <RouteOptionsSection record={record} />
          </QuoteSection>

          {/* The costing and the one thing done TO a costing here: it is priced.
              The order is the builder's pricing step, deliberately: the lines,
              then the markup that carries the sell up from them. Asking a
              colleague for the price is NOT here: it is the «Расчёт» card above
              the sections (S-2a), because the ask and its answer are the
              quote's state rather than a figure on the costing. */}
          <QuoteSection sectionKey="pricing">
            <CostLineSection
              record={record}
              target={costLineTarget}
              setTarget={setCostLineTarget}
            />
            <MarginPanel record={record} />
          </QuoteSection>

          {/* `editable`: this page asks the deal terms in one more place — the
              header sheet asks them too — and both save through the same
              whole-record PUT. The terms card is always mounted now, so its
              held edits are dropped once the record catches up with them
              (`useHeaderAmend`, L-6); a change made in the header sheet is
              therefore what the next save here sends. The SERVER still decides
              whether the window is open; see TermsSection. */}
          <QuoteSection sectionKey="terms">
            <TermsSection record={record} editable />
          </QuoteSection>

          {showPreviewSection ? (
            <QuoteSection sectionKey="preview">
              <QuotePreviewSection
                record={record}
                user={user}
                language={offerLanguage}
                onLanguageChange={setOfferLanguage}
                onOpenPreview={() => setPreviewOpen(true)}
                onSend={() => setSendOpen(true)}
                sendBlockedReason={sendRefusal}
                sendPending={send.isPending}
              />
            </QuoteSection>
          ) : null}
        </QuotePageColumns>

        {/* The preview can SEND, because the point of looking is to then act —
            and it hands off to the same sheet rather than sending on its own, so
            the recipient is still named before the mail leaves. */}
        <OfferPreviewSheet
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          preview={preview.data}
          pending={preview.isPending}
          error={preview.error}
          onRetry={() => void preview.refetch()}
          onSend={() => {
            setPreviewOpen(false)
            setSendOpen(true)
          }}
          /* The page's sentence, because the page knows what the preview does
             not: whether the reader may send to this client at all, and
             whether there is an address. The sheet words a refusal from the
             server's own `preview.refusal` when the preview carries one, so
             the button agrees with the banner above the document. */
          sendBlockedReason={sendRefusal}
          sendPending={send.isPending}
          language={offerLanguage}
          onLanguageChange={setOfferLanguage}
        />

        {/* The conversion confirmation (QW-11). Rendered whatever the status:
            a refusal that re-reads the quote into Converted or Draft must not
            unmount the sheet that is showing it. */}
        <ConvertQuoteSheet
          open={convertOpen}
          onClose={() => setConvertOpen(false)}
          onConfirm={(operationsOwnerId, paymentTermsCreditDays) =>
            convert.mutate({ quoteId, operationsOwnerId, paymentTermsCreditDays }, {
              // FR-21 in one gesture: the case is what the user wanted, so the
              // screen goes there rather than reporting that it exists. The
              // RESPONSE decides the note, not the preview: a case converted
              // without its planned leg says so on arrival rather than leaving
              // Operations to discover the missing leg.
              onSuccess: (result) => {
                setConvertOpen(false)
                navigate(`/cases/${result.caseId}`, {
                  state: result.legCreated === false ? { legSkipped: true } : undefined,
                })
              },
            })
          }
          /* Once a lost race re-reads the quote into Converted, the sheet offers
             the case the header now links to — the same destination. */
          onOpenCase={() => {
            setConvertOpen(false)
            if (record.caseId) navigate(`/cases/${record.caseId}`)
          }}
          pending={convert.isPending}
          error={convert.error ?? undefined}
          record={record}
          team={team.data}
        />

        <OfferSendSheet
          open={sendOpen}
          onClose={() => {
            setSendOpen(false)
            send.reset()
          }}
          quote={record}
          /* Whether the reader may read the client's contacts decides which of
             two refusals an absent address is (Q4). */
          user={user}
          /* The language the preview was read in, not a default of this sheet's
             own. Whoever opened the preview has already checked a page in one
             language; offering them a control that starts somewhere else is how
             an English offer gets issued in Russian by a reader who never touched
             the field. */
          language={offerLanguage}
          onLanguageChange={setOfferLanguage}
          onSubmit={(values) =>
            send.mutate(values, {
              // Closed only on success. A refusal leaves the sheet open with the
              // message on it, because the user has to decide what to do next —
              // and a sheet that vanished on failure would read as a send.
              onSuccess: () => setSendOpen(false),
            })
          }
          pending={send.isPending}
          /* sendErrorMessage rather than the generic one: a send refused because
             the issuing entity has no legal footer arrives as a validation
             problem on `issuingEntity`, a field this sheet does not render, so
             the generic wording would say "check the highlighted fields" of a
             form that has none. The fix is in Settings → Entities and only that
             sentence sends the reader there. */
          submitError={send.error ? sendErrorMessage(send.error, t) : undefined}
        />

        {/* The header editor (FR-4). Reuses the sheet the wizard's step 1 was
            built from rather than a third form asking the same questions: two
            copies of these fields would eventually disagree about whether "20 "
            is a number, one surface sending what the other refused.

            toQuoteUpdatePayload, NOT toQuotePayload. The PUT takes the whole
            record and reads an absent key as "clear it", and QuoteDraft carries
            only about half the wire's fields — so the create builder would have
            wiped the cargo owner, the pickup location, the carrier line and the
            FR-11 terms on the first save. That function starts from the record and
            overlays the draft, which is the only safe shape for a whole-record PUT
            reached from a partial form. */}
        <QuoteFormSheet
          open={editing}
          onClose={() => {
            setEditing(false)
            update.reset()
          }}
          record={record}
          pending={update.isPending}
          /* The refusal, raw. Until #75 nothing passed it, so a refused save
             left the sheet open and silent — and a contact that is not the
             client's (`clientContactId: invalid`) is said under its picker.
             The Edit press resets the mutation before opening, so this is
             never the previous open's refusal. */
          failure={update.error ?? undefined}
          onSubmit={(values) =>
            update.mutate(
              { id: quoteId, payload: toQuoteUpdatePayload(record, values) },
              // Closed only on success, exactly as the send sheet is: a refusal
              // leaves the sheet open holding what was typed, because UX-DR-18
              // forbids a failed request discarding the user's work.
              { onSuccess: () => setEditing(false) },
            )
          }
        />

        {/* The «⋯» menu's three views, each in a side sheet over the page.
            The sheet renders its body only while open, so a closed one reads
            nothing; the history is gated on `enabled` as well, because its
            query is the one that is a separate read of the action log. The
            team was fetched for the chat already (`useQuoteTeam` above), and
            its colleague roster loads only when Assign opens. */}
        <Sheet
          open={panel === 'revisions'}
          onClose={() => setPanel(null)}
          title={t('quotes.revisions.title')}
        >
          <RevisionsSection record={record} />
        </Sheet>
        <Sheet
          open={panel === 'history'}
          onClose={() => setPanel(null)}
          title={t('quotes.history.title')}
        >
          <QuoteHistorySection quoteId={quoteId} enabled={panel === 'history'} />
        </Sheet>
        {/* The INTERNAL team (Story 2.5, FR-14, FR-69), and only that. A quote
            names no external parties since S-22a: a counterparty appears where
            it acts, on the case's legs, trucks and cost lines. */}
        <Sheet
          open={panel === 'team'}
          onClose={() => setPanel(null)}
          title={t('quotes.team.title')}
        >
          <QuoteTeamSection record={record} />
        </Sheet>
      </QuotePage>

      {/* The dock at lg and up, the full-screen view below it — see ChatDock
          for why the two are one mount, and for why the dock may be dismissed
          now when the column it replaced could not be. Fixed in both shapes,
          so it takes part in no layout HERE; expanded at xl and up, AppShell's
          `<main>` reserves its width while it carries its marker (N-06, #127).
          Outside QuotePageColumns on purpose: that box is a size container,
          which may become the containing block of a fixed descendant.

          The number and the client are the dock's subtitle, off the record this
          page already holds. `unread` is the same count the header badge
          renders, from the same conversation row: the badge and the dock's
          minimised button must never disagree about whether somebody spoke.

          allowAttachments is FALSE: there is no quote file store, so a
          paperclip would upload bytes no message could ever point at.
          `archived` comes off the conversation row — a converted quote's thread
          stays readable and stops accepting messages, because the conversation
          continues on the case. */}
      {canChat ? (
        <ChatDock
          kind="quote"
          subjectId={record.id}
          conversationId={conversation?.id}
          subjectNumber={record.number}
          subjectClient={record.clientName}
          unread={conversation?.unread ?? 0}
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          allowAttachments={false}
          archived={conversation?.archived}
        />
      ) : null}
    </div>
  )
}
